import { useEffect, useRef, useState, useCallback, useMemo } from "react"

import { ColumnDef, ColumnFiltersState, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, SortingState, useReactTable, VisibilityState, RowSelectionState } from "@tanstack/react-table"
import { ContentObjectItem } from "@vertesia/common"
import { Loader2 } from "lucide-react"

import { ColumnFilter } from "@/components/datatables/filters/ColumnFilter"
import { DocumentOverlay } from "@/components/overlays/DocumentOverlay"
import { Checkbox } from "@ui/checkbox"
import { Skeleton } from "@ui/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ui/table"
import { useIntersectionObserver } from "@/plugins/hooks/useIntersectionObserver"
import { DynamicColumn } from "@/models/datatable"

function createDynamicColumns(dynamicColumns: DynamicColumn[], selectedIds: string[]): ColumnDef<ContentObjectItem>[] {
    const columns: ColumnDef<ContentObjectItem>[] = []

    dynamicColumns.forEach((dynCol) => {
        const getNestedValue = (obj: any, path: string) => {
            try {
                return path.split(".").reduce((current, key) => current?.[key], obj)
            } catch (error) {
                console.error("Error getting nested value:", error)
                return "No Data"
            }
        }

        const column: ColumnDef<ContentObjectItem> = {
            accessorKey: dynCol.key,
            header: ({ column }) => (
                <ColumnFilter
                    column={column}
                    label={dynCol.label}
                    hideFilter={true}
                />
            ),
            sortingFn: (rowA, rowB, _columnId) => {
                const valueA = getNestedValue(rowA.original, dynCol.key)
                const valueB = getNestedValue(rowB.original, dynCol.key)

                const stringA = String(valueA || "").toLowerCase()
                const stringB = String(valueB || "").toLowerCase()

                return stringA.localeCompare(stringB)
            },
            cell: ({ row }) => {
                if (dynCol.key === "title") {
                    return (
                        <DocumentOverlay document={row.original.id}>
                            <div className="underline text-blue-500 hover:cursor-pointer truncate">{(row.original as any)?.properties?.title || row.original?.name || row.original.id}</div>
                        </DocumentOverlay>
                    )
                }

                const value = getNestedValue(row.original, dynCol.key)

                if (dynCol.type === "date" && value) {
                    try {
                        const date = new Date(value)
                        if (!isNaN(date.getTime())) {
                            return <div>{date.toLocaleDateString()} {date.toLocaleTimeString()}</div>
                        }
                    } catch (error) {
                        // If date parsing fails, fall back to string display
                    }
                }

                return <div>{value}</div>
            },
            size: dynCol.size || 200,
            enableSorting: true,
            enableColumnFilter: false
        }

        columns.push(column)
    })

    columns.push({
        id: "select",
        header: ({ table }) => {
            const availableRows = table.getRowModel().rows.filter((row: any) => !selectedIds.includes(row.original.id))
            const availableRowsSelected = availableRows.filter((row: any) => row.getIsSelected())

            const isAllAvailableSelected = availableRows.length > 0 && availableRowsSelected.length === availableRows.length
            const isSomeAvailableSelected = availableRowsSelected.length > 0 && availableRowsSelected.length < availableRows.length

            return (
                <Checkbox
                    checked={isAllAvailableSelected || (isSomeAvailableSelected && "indeterminate")}
                    onCheckedChange={(value) => {
                        availableRows.forEach((row: any) => {
                            row.toggleSelected(!!value)
                        })
                    }}
                    aria-label="Select all"
                />
            )
        },
        cell: ({ row }) => {
            return (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                    disabled={selectedIds.includes(row.original.id)}
                />
            )
        },
        enableSorting: false,
        enableHiding: false,
        size: 50,
    })

    return columns
}

interface ProjectDataTableProps {
    data: ContentObjectItem[]
    loading: boolean
    loadMore?: () => void
    hasMore?: boolean
    loadingMore?: boolean
    onMultipleSelection: (documents: ContentObjectItem[]) => void
    selection?: ContentObjectItem[]
    selectedIds?: string[]
    hasSearched?: boolean
    noFilterMatch?: boolean
    columns: DynamicColumn[]
}
export function ProjectDataTable({ data, loading, loadMore, hasMore = false, loadingMore = false, onMultipleSelection, selection = [], selectedIds = [], hasSearched = false, noFilterMatch = false, columns: dynamicColumns }: Readonly<ProjectDataTableProps>) {
    const [sorting, setSorting] = useState<SortingState>([])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
    const tableContainerRef = useRef<HTMLDivElement>(null)
    const loadMoreRef = useRef<HTMLTableRowElement>(null)

    const updatingSelection = useRef(false)

    const columns = useMemo(() => createDynamicColumns(dynamicColumns, selectedIds), [dynamicColumns, selectedIds])

    const selectionIds = useMemo(() => {
        return new Set(selection.map(item => item.id))
    }, [selection])

    useEffect(() => {
        if (updatingSelection.current) return

        const newRowSelection: RowSelectionState = {}

        data.forEach((item, index) => {
            if (selectionIds.has(item.id)) {
                newRowSelection[index] = true
            }
        })

        const currentSelectedIndices = Object.keys(rowSelection).filter(key => rowSelection[key])
        const newSelectedIndices = Object.keys(newRowSelection).filter(key => newRowSelection[key])

        if (currentSelectedIndices.length !== newSelectedIndices.length ||
            !currentSelectedIndices.every(index => newRowSelection[index])) {
            setRowSelection(newRowSelection)
        }
    }, [data, selectionIds])

    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onRowSelectionChange: (updater) => {
            updatingSelection.current = true
            setRowSelection(updater)

            setTimeout(() => {
                updatingSelection.current = false
            }, 0)
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection
        },
        enableRowSelection: true,
        enableMultiRowSelection: true,
        enableMultiSort: true,
        columnResizeMode: "onChange"
    })

    useEffect(() => {
        const currentlySelectedRows = table.getFilteredSelectedRowModel().rows.map(row => row.original)
        const dataIds = new Set(data.map(item => item.id))

        // Keep previously selected items that are no longer in the current data
        const preservedSelection = selection.filter(item => !dataIds.has(item.id))

        // Combine preserved selection with currently selected rows (avoiding duplicates)
        const mergedSelection = [
            ...preservedSelection,
            ...currentlySelectedRows.filter(row => !preservedSelection.some(p => p.id === row.id))
        ]

        // Only update if selection actually changed
        const prevIds = new Set(selection.map(s => s.id))
        const newIds = new Set(mergedSelection.map(s => s.id))
        const hasChanged = prevIds.size !== newIds.size ||
            ![...prevIds].every(id => newIds.has(id))

        if (hasChanged) {
            onMultipleSelection(mergedSelection)
        }
    }, [rowSelection, onMultipleSelection, table, data, selection])

    const handleScroll = useCallback(() => {
        if (!tableContainerRef.current || loading || loadingMore || !hasMore || !loadMore) {
            return
        }

        const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current
        const diff = scrollHeight - scrollTop - clientHeight

        const threshold = Math.max(50, clientHeight * 0.1)

        if (diff < threshold) {
            loadMore()
        }
    }, [loading, loadingMore, hasMore, loadMore])

    const handleIntersection = useCallback(() => {
        if (!loading && !loadingMore && hasMore && loadMore) {
            loadMore()
        }
    }, [loading, loadingMore, hasMore, loadMore])

    useEffect(() => {
        const tableContainer = tableContainerRef.current
        if (tableContainer) {
            tableContainer.addEventListener("scroll", handleScroll)

            return () => {
                tableContainer.removeEventListener("scroll", handleScroll)
            }
        }
    }, [handleScroll])

    useIntersectionObserver(loadMoreRef, handleIntersection, {
        deps: [data.length, hasMore, loadingMore, loading],
        threshold: 0.1
    })

    const showInitialLoading = loading && data.length === 0
    const showDataRows = !showInitialLoading && table.getRowModel().rows?.length > 0
    const showNoResults = !loading && hasSearched && data.length === 0 && !noFilterMatch
    const showNoFilterMatch = !loading && noFilterMatch

    return (
        <div className="flex flex-col h-full w-full">
            <div ref={tableContainerRef} className="flex-1 border rounded-md border-gray bg-white" style={{ overflow: 'auto', position: 'relative' }}>
                <div style={{ minHeight: '100%' }}>
                    <Table className="w-full" style={{ height: '100%' }}>
                        <TableHeader className="bg-white after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-gray-200" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            {
                                table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead
                                                key={header.id}
                                                style={{
                                                    maxWidth: header.getSize(),
                                                    position: 'sticky',
                                                    top: 0,
                                                    backgroundColor: 'white',
                                                    zIndex: 10
                                                }}
                                            >
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )
                                                }
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))
                            }
                        </TableHeader>
                        <TableBody>
                            {
                                (showInitialLoading) && (
                                    <TableRow>
                                        <TableCell colSpan={dynamicColumns.length + 2}>
                                            <div className="flex justify-center items-center w-full h-full">
                                                <div className="w-full h-full space-y-2">
                                                    {[...Array(50)].map((_, index) => (
                                                        <Skeleton key={index} className="h-12 w-full" />
                                                    ))}
                                                </div>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            }
                            {
                                showDataRows && table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() ? "selected" : undefined}
                                        className={`${selectedIds.includes(row.original.id) ? "bg-gray-50" : ""} ${row.getIsSelected() ? "bg-blue-50" : ""}`}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id} style={{ maxWidth: cell.column.getSize() }}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            }
                            {
                                (showNoResults) && (
                                    <TableRow>
                                        <TableCell colSpan={dynamicColumns.length + 2}>
                                            <div className="flex justify-center items-center w-full">
                                                <p className="text-gray-500 text-center">No documents found.</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            }
                            {
                                (showNoFilterMatch) && (
                                    <TableRow>
                                        <TableCell colSpan={dynamicColumns.length + 2}>
                                            <div className="flex justify-center items-center w-full py-8">
                                                <p className="text-gray-500 text-center">No documents match the current filtering logic.</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            }
                            {
                                (!loading && !hasSearched && data.length === 0 && !noFilterMatch) && (
                                    <TableRow>
                                        <TableCell colSpan={dynamicColumns.length + 2}>
                                            <div className="flex justify-center items-center w-full py-8">
                                                <p className="text-gray-500 text-center">Please select at least one value in any Search By filter to see results.</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            }
                            {
                                (loadingMore) && (
                                    <TableRow>
                                        <TableCell colSpan={dynamicColumns.length + 2}>
                                            <div className="flex justify-center items-center p-4">
                                                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            }
                            {
                                (hasMore && loadMore && !loadingMore && data.length > 0) && (
                                    <TableRow ref={loadMoreRef}>
                                        <TableCell colSpan={dynamicColumns.length + 2} className="h-20 p-0" />
                                    </TableRow>
                                )
                            }
                            <TableRow style={{ height: '100%' }}>
                                <TableCell colSpan={dynamicColumns.length + 2} style={{ height: '100%', padding: 0, border: 0 }} />
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>
            <div className="flex items-center justify-end space-x-2 pt-2 text-sm text-muted-foreground">
                {showInitialLoading
                    ? "Loading documents..."
                    : loadingMore
                        ? "Loading more..."
                        : (data.length === 0 && !hasSearched)
                            ? "Select filters to search..."
                            : data.length === 0
                                ? "No results."
                                : <>
                                    {table.getFilteredRowModel().rows.length} document{table.getFilteredRowModel().rows.length <= 1 ? "" : "s"}.
                                    {!hasMore ? " (All results loaded)" : ""}
                                </>
                }
            </div>
        </div>
    )
}